/**
 * Типы ответов API. Дублируем Pydantic-схемы из backend (src/inflow/api/schemas.py).
 * При расхождении — runtime ошибки в TanStack Query компонентах, что хорошо для отладки.
 */

export interface MeOut {
  user_id: number;
  tg_username: string | null;
  first_name: string | null;
  last_name: string | null;
  language_code: string | null;
  is_admin: boolean;
  has_active_subscription: boolean;
  // Юзер погодився отримувати безкоштовні вакансії (натиснув у попапі). Owner 2026-07-09.
  free_optin: boolean;
  // Free-доставку поставлено на паузу (тиждень не заходив). Owner 2026-07-10.
  free_paused: boolean;
  is_banned: boolean;
  // Onboarding-флаги (юзер 2026-05-26: Mini App блокується до проходження)
  subscribed_main_channel: boolean;
  has_categories: boolean;
  onboarding_completed: boolean;
  /** Доступ до підбірки відкрито (підписка на канал / платна / адмін). */
  selection_unlocked: boolean;
  // Ключі пройдених інструкцій-підказок Mini App ('basic' + розділи).
  // Окремо від старої колонки instruction_done.
  seen_hints: string[];
  // Стан колеса їде РАЗОМ із профілем: окремий запит спізнювався, і напис на
  // картці моргав (owner 2026-09-05).
  wheel_available?: boolean;
  wheel_next_grant_at?: string | null;
  wheel_spins_left?: number;
  wheel_free_left?: number;
}

export interface CategoryOut {
  id: number;
  slug: string;
  name_uk: string;
  sort_order: number;
}

export interface CategoriesListOut {
  items: CategoryOut[];
  selected_ids: number[];
}

export interface SetCategoriesIn {
  category_ids: number[];
}

export interface SubscriptionOut {
  id: number;
  plan: string;
  status: string;
  started_at: string;
  ends_at: string;
  is_recurring: boolean;
  days_left: number;
  freeze_days_balance: number;
  /** Заморозка: до якої дати і на скільки днів (owner 2026-08-10). */
  frozen_until?: string | null;
  frozen_days_total?: number;
  /** Заморожена з датою кінця паузи: коли підписка скінчиться після
   *  розморозки. `ends_at` під час паузи стоїть на місці й може вже минути,
   *  а `days_left` тоді — дні, що лишаться після розморозки (owner 2026-09-26).
   *  Безстрокова заморозка — null. */
  ends_at_after_unfreeze?: string | null;
}

export interface NoSubscriptionOut {
  active: false;
  has_used_test: boolean;
  freeze_days_balance: number;
}

export type SubscriptionMeResponse = SubscriptionOut | NoSubscriptionOut;

export function isActiveSubscription(s: SubscriptionMeResponse): s is SubscriptionOut {
  return "id" in s;
}

export interface TariffOut {
  slug: string;
  title_uk: string;
  plan: string;
  days: number;
  amount: string;
  description_uk: string;
  is_recurring_default: boolean;
  available: boolean;
}

export interface TariffsListOut {
  items: TariffOut[];
}

// ============================================================
// Profile
// ============================================================

export interface ProfileOut {
  exists: boolean;
  full_name: string | null;
  specialty: string | null;
  experience_years: number | null;
  skills: string[];
  /** Чорний список слів (owner 2026-09-06): вакансії з ними не приходять. */
  blocked_words: string[];
  bio: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  birthday: string | null; // YYYY-MM-DD
  /** Чи можна ще змінити вже вказану дату (лише один раз, owner 2026-09-07). */
  birthday_change_left?: boolean;
  portfolio_text: string | null;
  portfolio_url: string | null;
  portfolio_photos: string[];
  is_published: boolean;
  // Кастомний AI-стиль (1 на юзера, юзер 2026-06-25)
  ai_style_name: string | null;
  ai_style_prompt: string | null;
}

export type ProfileUpdateIn = Partial<{
  full_name: string | null;
  specialty: string | null;
  experience_years: number | null;
  skills: string[];
  blocked_words: string[];
  bio: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  birthday: string | null;
  portfolio_text: string | null;
  portfolio_photos: string[];
}>;

// ============================================================
// Referrals
// ============================================================

export interface ReferralItem {
  user_id: number;
  first_name: string | null;
  tg_username: string | null;
  created_at: string;
}

export interface ReferralsOut {
  balance_uah: number;
  regular_balance_uah: number;
  total_earned: number;
  regular_total_earned: number;
  total_spent: number;
  referrer_user_id: number | null;
  referrals_count: number;
  referral_link: string;
  recent_referrals: ReferralItem[];
  /** Розділ «Мої посилання» (owner 2026-10-25) — лише для окремих людей. */
  links_enabled?: boolean;
}

/** Цифри одного посилання («як у Findly»): прийшли → зараховані → тест → оплатили. */
export interface ReferralLinkStats {
  came: number;
  credited: number;
  trials: number;
  payers: number;
  earned: number;
}

export interface ReferralLink {
  id: number;
  code: string;
  name: string;
  link: string;
  created_at: string;
  archived_at: string | null;
  active: boolean;
  stats: ReferralLinkStats;
}

export interface ReferralLinksOut {
  enabled: boolean;
  max: number;
  links: ReferralLink[];
  total: ReferralLinkStats;
}

// ============================================================
// Quests
// ============================================================

export interface QuestItemOut {
  id: string;
  group: "referrals" | "ai" | "community" | "subscription";
  title: string;
  caption: string;
  reward_uah: number;
  threshold: number | null;
  progress: number | null;
  is_done: boolean;
}

export interface QuestsListOut {
  items: QuestItemOut[];
  regular_balance_uah: number;
  regular_expires_at: string | null;
}

// Підписочні квести (підписка на канали/чат → +50₴ кожен)
export interface SubscriptionQuestItemOut {
  key: string;
  title: string;
  caption: string;
  username: string;
  kind: "channel" | "chat";
  avatar_url: string;
  reward_uah: number;
  is_done: boolean;
}

export interface SubscriptionQuestsOut {
  items: SubscriptionQuestItemOut[];
  total_reward_uah: number;
  earned_reward_uah: number;
  regular_balance_uah: number;
}

export interface SubscriptionCheckOut {
  status: "credited" | "already" | "not_subscribed" | "unknown_key";
  credited_uah: number;
  is_done: boolean;
  regular_balance_uah: number;
  message: string;
}

// ============================================================
// Course / Knowledge base
// ============================================================

export interface LessonOut {
  id: number;
  category_id: number | null;
  lesson_category_label: string | null;
  title: string;
  description: string | null;
  duration: string | null;
  speaker_info: string | null;
  speaker_url: string | null;
  video_file_id: string;
  sort_order: number | null;
  average_rating: number | null;
  rating_count: number;
  my_rating: number | null;
}

export interface LessonsListOut {
  items: LessonOut[];
  has_access: boolean;
}

// ============================================================
// База знань — 4 розділи 1:1 з 1.0
// ============================================================

export interface KBSectionOut {
  id: string; // "articles" | "podcasts" | "lessons" | "become_speaker"
  title: string;
  kind: "articles" | "external" | "lessons" | "become_speaker";
  external_url: string | null;
  text: string | null;
  /** Прогрес перегляду уроків — лише для розділу уроків. */
  viewed_count?: number | null;
  total_count?: number | null;
}

export interface KBSectionsResponse {
  welcome_text: string;
  sections: KBSectionOut[];
}

/** `/knowledge-base/counts` — скільки матеріалів у Базі знань. */
export interface KBCountsOut {
  articles: number;
  podcasts: number;
  lessons: number;
}

export interface KBArticleItemOut {
  title: string;
  slug: string;
  url: string;
}

export interface KBArticleCategoryOut {
  idx: string;
  name: string;
  items: KBArticleItemOut[];
}

export interface KBLessonCategoryOut {
  idx: number;
  name: string;
  count: number;
}

export interface KBLessonItemOut {
  id: number;
  category_label: string | null;
  title: string;
  description: string | null;
  duration: string | null;
  speaker_info: string | null;
  speaker_url: string | null;
  video_file_id: string;
  /** Публічний URL до .mp4 через Caddy. null якщо ще не скачано. */
  video_url: string | null;
  average_rating: number | null;
  rating_count: number;
  /** Поточна оцінка користувача (1-5) або null. */
  my_rating: number | null;
  /** Чи переглянула людина урок (15+ секунд) — галочка на карточці. */
  viewed: boolean;
  /** Матеріали під відео — PDF тощо (owner 2026-10-21). Старий API поля не
   *  віддає, тому необовʼязкове. */
  materials?: KBLessonMaterialOut[];
}

export interface KBLessonMaterialOut {
  name: string;
  mime: string | null;
  size: number | null;
  /** Підписане посилання на файл — відкривається без initData. */
  url: string;
}

export interface VoteLessonOut {
  lesson_id: number;
  rating: number;
  bonus_credited: boolean;
}

export interface KBLessonsListOut {
  items: KBLessonItemOut[];
  /** «Переглянуто N з M» у шапці розділу (owner 2026-09-17). */
  viewed_count: number;
  total_count: number;
}

// ============================================================
// Admin
// ============================================================

export interface AdminStatsOut {
  total_users: number;
  active_users: number;
  new_users_today: number;
  new_users_7d: number;
  active_subscriptions: number;
  frozen_subscriptions: number;
  test_subscriptions: number;
  total_referrals: number;
  pending_broadcasts: number;
  // Free-tier сегменти (owner 2026-07-10, крок 7)
  free_optin_active: number;
  free_optin_paused: number;
  free_not_optin: number;
  paid_users: number;
}

// --- Admin dashboard ---
export type AdminRange =
  | "today"
  | "yesterday"
  | "3d"
  | "7d"
  | "30d"
  | "90d"
  | "all";

export interface DashboardSubsBlock {
  active: number;
  autorenew_on: number;
  autorenew_off: number;
  frozen: number;
  test: number;
}

export interface DashboardUsersBlock {
  total: number;
  active: number;
  inactive: number;
  onboarded: number;
  new_in_range: number;
  onboarded_in_range: number;
}

/** Розріз живої бази (owner 2026-08-27): тільки ті, у кого бот не вимкнений. */
export interface DashboardSegments {
  alive: number;
  paid_active: number;
  paid_expired: number;
  never_paid: number;
  free_receiving: number;
  free_paused: number;
  free_silent_30d: number;
  free_no_optin: number;
  onboarded: number;
  no_spheres: number;
  active_7d: number;
  active_30d: number;
  new_7d: number;
}

export interface DashboardSourceCount {
  source: string;
  count: number;
}

export interface DashboardSphereCount {
  category_id: number;
  name_uk: string;
  count: number;
}

export interface DashboardUsersBySource {
  telegram: number;
  chats: number;
  threads: number;
  fhunt: number;
  finder: number;
}

export interface DashboardSphereUsers {
  category_id: number;
  name_uk: string;
  users: number;
}

export interface DashboardTimePoint {
  date: string;
  count: number;
}

export interface AdminDashboardOut {
  range: string;
  cohort: string;
  subscriptions: DashboardSubsBlock;
  users: DashboardUsersBlock;
  subs_issued_in_range: number;
  vacancies_by_source: DashboardSourceCount[];
  vacancies_by_sphere: DashboardSphereCount[];
  segments: DashboardSegments;
  users_by_source: DashboardUsersBySource;
  users_by_sphere: DashboardSphereUsers[];
  vacancies_timeseries: DashboardTimePoint[];
  users_timeseries: DashboardTimePoint[];
}

export interface AdminFunnelStage {
  key: string;
  label: string;
  n: number;
  pct: number;
  pct_prev: number | null;
  trial: number | null;
  paid: number | null;
}

export interface AdminFunnelOut {
  range: string;
  cohort_size: number;
  stages: AdminFunnelStage[];
}

export interface AdminProfileSubscription {
  plan: string;
  source: string;
  status: string;
  started_at: string;
  ends_at: string;
  is_recurring: boolean;
  /** Заморозка поточної підписки (owner 2026-08-10). */
  frozen_until?: string | null;
  frozen_since?: string | null;
}

export interface AdminProfilePayment {
  order_reference: string;
  kind: string;
  amount: number;
  status: string;
  bonus_used: number;
  paid_at: string | null;
  created_at: string;
  /** Платіжна система: wfp | mono (owner 2026-10-19). */
  provider?: string;
}

/** Чим продовжується підписка (owner 2026-10-19): договір mono чи WayForPay. */
export interface AdminProfileRenewal {
  provider: "mono" | "wfp" | null;
  card_mask: string | null;
  retry_n: number;
  /** Наступна спроба списання mono, YYYY-MM-DD (за Києвом). */
  next_charge: string | null;
  amount_uah: number | null;
  stopped_reason: string | null;
}

export interface AdminProfileSources {
  telegram: boolean;
  chats: boolean;
  threads: boolean;
  fhunt: boolean;
  finder: boolean;
}

export interface AdminUserProfileOut {
  user_id: number;
  tg_username: string | null;
  first_name: string | null;
  last_name: string | null;
  language_code: string | null;
  registered_at: string;
  last_seen_at: string | null;
  blocked_at: string | null;
  is_banned: boolean;
  onboarding_completed: boolean;
  categories: string[];
  sources: AdminProfileSources;
  referral_balance_uah: number;
  regular_balance_uah: number;
  total_earned: number;
  regular_total_earned: number;
  total_spent: number;
  referrals_invited: number;
  referrals_paid: number;
  quests_done: number;
  current_subscription: AdminProfileSubscription | null;
  subscriptions_total: number;
  subscriptions: AdminProfileSubscription[];
  payments_total: number;
  payments_approved: number;
  total_paid_uah: number;
  payments: AdminProfilePayment[];
  renewal?: AdminProfileRenewal | null;
  profile_bio: string | null;
  profile_specialty: string | null;
  profile_birthday: string | null; // YYYY-MM-DD
  /** Звернення в підтримку (S29). null — блок не прочитався на сервері. */
  support?: AdminProfileSupport | null;
}

export interface AdminProfileSupportItem {
  at: string;
  question: string;
  reply: string;
  error: string;
}

/** Одне повідомлення листування в лічці (`support_dialog`, owner 2026-10-01). */
export interface AdminProfileDialogItem {
  at: string;
  /** in — людина, owner — власник рукою, bot — відповідь AI. */
  direction: "in" | "owner" | "bot" | string;
  text: string;
  kind: string;
  edited_at: string | null;
  deleted_at: string | null;
}

export interface AdminProfileSupport {
  requests_total: number;
  requests_unanswered: number;
  inbox_total: number;
  inbox_answered: number;
  inbox_skipped: number;
  inbox_failed: number;
  inbox_pending: number;
  /** Вхідні в лічку без відповіді, без свідомої тиші. */
  inbox_unanswered: number;
  escalated: number;
  last_at: string | null;
  reasons: { reason: string; count: number }[];
  recent: AdminProfileSupportItem[];
  /** Листування в лічці: людина / власник рукою / бот (з 01.10.2026). */
  dialog_in?: number;
  dialog_owner?: number;
  dialog_bot?: number;
  dialog?: AdminProfileDialogItem[];
  dialog_failed?: boolean;
}

/** «Журнал дій» людини для банку (адмінка → Чарджбеки, S57). */
export interface UserJournalEvent {
  at: string;
  /** Час за Києвом, уже відформатований сервером. */
  when: string;
  source: string;
  label: string;
  text: string;
  day_only: boolean;
}

export interface UserJournalOut {
  user_id: number;
  username: string;
  name: string;
  total: number;
  truncated: boolean;
  /** Джерела, які не прочитались (ключі й назви для людини). */
  failed: string[];
  failed_titles?: string[];
  events: UserJournalEvent[];
  /** Готовий текст для «Скопіювати». */
  text: string;
}

/** Підсумок зняття регулярок WayForPay (бан, прохання банку). */
export interface RegularRemovalOut {
  ok: boolean;
  checked?: string[];
  removed?: string[];
  not_active?: string[];
  failed?: { ref: string; error: string }[];
  flags_off?: number;
  current?: string;
  skipped?: string[];
  skipped_reason?: string;
  summary: string;
  error?: string;
}

export interface AdminPromoItem {
  slug: string;
  name: string;
  days: number;
  amount: number;
  is_active: boolean;
  redemptions: number;
  /** Скільки людей відкрило посилання (перейшли). */
  clicks: number;
  link: string;
}

export interface AdminPromoListOut {
  bot_username: string;
  items: AdminPromoItem[];
}

export interface AdminUserItem {
  user_id: number;
  tg_username: string | null;
  first_name: string | null;
  last_name: string | null;
  language_code: string | null;
  registered_at: string;
  last_seen_at: string | null;
  blocked_at: string | null;
  has_active_subscription: boolean;
  referral_balance_uah: number;
  regular_balance_uah: number;
}

export interface AdminUserSearchOut {
  items: AdminUserItem[];
  total_matched: number;
}

export interface BroadcastOut {
  id: number;
  status: string;
  segment: string;
  message_text: string;
  photo_url: string | null;
  targets_count: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface BroadcastListOut {
  items: BroadcastOut[];
}
